// supporting /measure urls but immediately redirecting to the specific unit converter page based on state
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { MeasureDataContext } from '../contexts/MeasureDataProvider'
import { AppContext } from '../contexts/AppProvider'
import { useAppNavigate } from '../hooks/useAppNavigate'
import { getMeasureFromUrl, isValidMeasureUrl } from '../utils/measureHelpers'
import { Measure } from '../types'
import SEOHelmet from '../components/SEOHelmet'
import { generateMeasurePageSEOProps } from '../utils/seo'

const MeasureRedirectorPage: React.FC = () => {
  const { measure: newMeasureUrl } = useParams()
  const { allMeasures } = React.useContext(MeasureDataContext)
  const { appState } = React.useContext(AppContext)
  const { navigateToConverter, navigateToNotFound } = useAppNavigate()

  const currentMeasure: Measure | null = React.useMemo(() =>
    newMeasureUrl && isValidMeasureUrl(allMeasures, newMeasureUrl)
      ? getMeasureFromUrl(allMeasures, newMeasureUrl)
      : null,
    [allMeasures, newMeasureUrl]
  )

  // Generate SEO props using your utility function
  const seoProps = React.useMemo(() =>
    currentMeasure ? generateMeasurePageSEOProps(currentMeasure, currentMeasure.defaultFromUnit, currentMeasure.defaultToUnit) : null,
    [currentMeasure]
  )

  useEffect(() => {
    if (newMeasureUrl === undefined || !isValidMeasureUrl(allMeasures, newMeasureUrl)) {
      return navigateToNotFound()
    }
    const newCurrentMeasure: Measure = getMeasureFromUrl(allMeasures, newMeasureUrl)

    // Get the last used units for this measure from app state
    const fromUnit = appState.tabs[newCurrentMeasure.codeName].fromUnit
    const toUnit = appState.tabs[newCurrentMeasure.codeName].toUnit

    // Redirect to the specific converter
    navigateToConverter(newCurrentMeasure, fromUnit, toUnit)
  }, [newMeasureUrl, allMeasures, appState.tabs, navigateToConverter, navigateToNotFound])

  // Return null as this is just a redirector
  return seoProps ? <SEOHelmet {...seoProps} /> : null
}

export default MeasureRedirectorPage
